import type { JSX, ChangeEvent } from "react";
import { useState } from "react";

import type { IProModal } from "pro-features/components/Modals/src/ProModal";
import { getInitialSharingState } from "app/pages/dashboard/SharedDashboardModal/utils";
import type {
    ISharingModalState,
    ISharingModalOnSubmit,
    ISharingModalTextContainer,
} from "app/components/SharingModal/types";
import { ISharingModalOption } from "app/components/SharingModal/types";
import type { IShareUserExtended } from "app/sharing/SharingService";
import { SharingModal } from "app/components/SharingModal/SharingModal";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { SharingModalBanner } from "app/components/SharingModal/components/SharingModalBanner/SharingModalBanner";
import { Footer } from "app/components/SharingModal/components/Footer/Footer";

interface ISharedDashboardModalProps {
    onSubmit: ISharingModalOnSubmit;
    modalText: ISharingModalTextContainer;
    sharedWithUsers: number[];
    sharedWithInvitations: number[];
    initialSharingState: ISharingModalOption;
    setIsOpen: (isOpen: boolean) => void;
    isSharingDashboard: boolean;
    users: IShareUserExtended[];
}

const defaultModalProps: IProModal = {
    showCloseIcon: false,
    customStyles: { content: { padding: 0, width: "auto" } },
};

export const SharedDashboardModal = ({
    initialSharingState = ISharingModalOption.SHARED,
    modalText,
    onSubmit,
    sharedWithUsers: initialSharedWithUsers = [],
    sharedWithInvitations,
    setIsOpen,
    isSharingDashboard,
    users,
}: ISharedDashboardModalProps): JSX.Element => {
    const hasSmartSharing = swSettings.components.ShareDashboard.resources.IsDashboardsSmartSharingEnabled;
    const { header, sharingOptionTextContainer } = modalText;
    const [sharing, setSharing] = useState<ISharingModalState>(
        getInitialSharingState(initialSharingState, initialSharedWithUsers, modalText, sharedWithInvitations),
    );

    const handleSubmitOrClose = () => (isChanged ? onSubmit(sharing, handleCloseModal) : handleCloseModal());

    const handleCloseModal = (): void => {
        setIsOpen(false);
        setSharing({
            ...sharing,
            sharedWithUsersToShow: [...sharing.sharedWithUsers, ...sharing.sharedWithInvitations],
        });
    };

    const modalProps: IProModal = {
        ...defaultModalProps,
        onCloseClick: handleCloseModal,
        isOpen: true,
    };

    const onShare = (sharingState: ISharingModalState): void => {
        setSharing(sharingState);
    };

    const handleChangeNotification = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        setSharing({ ...sharing, notification: event.target.value });
    };

    const isBannerShown = !hasSmartSharing;
    const isChanged =
        initialSharingState !== sharing.sharingState || initialSharedWithUsers !== sharing.sharedWithUsers;
    const shareButtonText = isSharingDashboard
        ? translate("dashboard.shareDashboard.modal.btn.saving")
        : isChanged
          ? translate("dashboard.shareDashboard.modal.btn.save")
          : translate("dashboard.shareDashboard.modal.btn.close");

    return (
        <SharingModal
            modalProps={modalProps}
            headerProps={header}
            text={sharingOptionTextContainer}
            users={users}
            sharing={sharing}
            isRestrictedOptionDisabled={isBannerShown}
            isSharedOptionDisabled={isSharingDashboard}
            isBannerShown={isBannerShown}
            onShare={onShare}
            onChangeNotification={handleChangeNotification}
            bannerComponent={
                <SharingModalBanner
                    hasCustomIcon={true}
                    hasCustomButton={true}
                />
            }
            footerComponent={
                <Footer
                    onClose={handleSubmitOrClose}
                    isLoadingSharing={isSharingDashboard}
                    shareButtonText={shareButtonText}
                />
            }
        />
    );
};
